import { components } from '@lifeserver/models/iapv2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { IAP_PRICING_BASE_PATH } from '../utils/pathsConstants';
import { handleError } from '../utils/utils';

type IapProductBase = components['schemas']['IAPProductEntity'];

// iapv2 returns these as nullable in practice (rows can have null id,
// iap_type and storefront_status), so widen them over the generated model.
export type IapProduct = Omit<
    IapProductBase,
    'id' | 'type' | 'storefrontStatus'
> & {
    id?: number | null;
    type?: string | null;
    storefrontStatus?: IapProductBase['storefrontStatus'] | null;
};

export type SkuStatusResult = {
    sku: string;
    status: 'PUBLISHED' | 'NOT_PUBLISHED' | 'NOT_FOUND' | 'ERROR';
    error: string;
};

const QUERY_KEY_IAP_PRODUCTS = 'iap-products';

async function fetchIapProducts(): Promise<IapProduct[]> {
    const { data } = await axios.get<IapProduct[]>(
        `/api/${IAP_PRICING_BASE_PATH}/products`,
    );
    return data;
}

type SyncVariables = { skus: string[]; store: string };

async function syncSkuStatuses(
    variables: SyncVariables,
): Promise<{ skus: SkuStatusResult[] }> {
    const storePath = variables.store === 'apple' ? 'applestore' : 'googleplay';
    const { data } = await axios.patch<{ skus: SkuStatusResult[] }>(
        `/api/${IAP_PRICING_BASE_PATH}/${storePath}/inapppurchases/sku-status/sync`,
        { skus: variables.skus },
    );
    return data;
}

export function useIapProducts() {
    const { data, isPending, error, refetch } = useQuery({
        queryKey: [QUERY_KEY_IAP_PRODUCTS],
        queryFn: fetchIapProducts,
    });

    return {
        products: data,
        isLoading: isPending,
        error: error
            ? handleError("Couldn't load SKUs", error, false)
            : undefined,
        refetch,
    };
}

export function useSyncIapProductStatuses() {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, data, isPending, error } = useMutation({
        mutationFn: syncSkuStatuses,
        onSuccess: (result, variables) => {
            queryClient.setQueryData<IapProduct[]>(
                [QUERY_KEY_IAP_PRODUCTS],
                (old) => {
                    if (!old) {
                        return old;
                    }
                    const statusBySku = new Map(
                        result.skus.map((s) => [s.sku, s.status]),
                    );
                    return old.map((product) => {
                        if (product.store !== variables.store) {
                            return product;
                        }
                        const status = statusBySku.get(product.sku);
                        return status
                            ? { ...product, storefrontStatus: status }
                            : product;
                    });
                },
            );
        },
    });

    return {
        syncStatuses: mutate,
        syncStatusesAsync: mutateAsync,
        result: data,
        isSyncing: isPending,
        errorMessage: error
            ? handleError("Couldn't sync SKU statuses", error, false)
            : undefined,
    };
}
